import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, CircularProgress, Button, Tabs, Tab } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ContentArea from '../components/content-area/Content';
import PackageItemCard from '../components/items/PackageItemCard';
import ItemCard from '../components/items/ItemCard';
import ItemModal from '../components/items/ItemModal';
import ConfirmModal from '../components/form-components/ConfirmModal';
import StatusTabs from '../components/approval/StatusTabs';
import LocalText from '../components/common/LocalText';
import { useSpaceId } from '../hooks/useSpaceId';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../utils/versionedContent';
import { CONTENT_TYPES } from '../constants/contentTypes';
import {
  getItems, getItemTypes, getTaxCategories, createTaxClass,
  createItem, updateItem, deleteItem, getItemById,
} from '../api/itemApi';
import { submitApproval } from '../api/approvalApi';
import { getPackageById, getPackageItems, addPackageItem, removePackageItem } from '../api/packageApi';

// Hotfix (client requirement, 2026-09-19): package membership is restricted to
// Avatar items only, and Avatar items are only creatable from this page (Items.jsx
// hides Avatar entirely). Revert by removing the itemTypeId === AVATAR_ITEM_TYPE_ID
// filters below and the "Create New Avatar" tab — add/remove/approval stay generic.
const AVATAR_ITEM_TYPE_ID = 6;

export default function PackageAddItems() {
  const spaceId = useSpaceId();
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/items', 'package-edit');
  // Creating/editing the underlying Avatar item reuses the same permission as
  // item creation on the Items page — not everyone who can edit packages should
  // be able to create catalog items.
  const canCreateAvatar = canFeature('/items', 'items-edit');

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [taxCategories, setTaxCategories] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [packageItems, setPackageItems] = useState([]);
  const [search, setSearch] = useState('');
  const [busyItemId, setBusyItemId] = useState(null);
  const [tab, setTab] = useState('select'); // 'select' | 'create'

  // Tab 2 — status sub-tabs (Live/Pending/Draft/Rejected), same as Items.jsx,
  // so avatar management here feels identical to the Items page.
  const [avatarStatusTab, setAvatarStatusTab] = useState('live');
  const [avatarHighlight, setAvatarHighlight] = useState({ tab: null, token: 0 });
  const flashAvatarTab = useCallback((t) => setAvatarHighlight({ tab: t, token: Date.now() }), []);

  // Tab 2 — Avatar create/edit/approval modal state
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarModalReadOnly, setAvatarModalReadOnly] = useState(false);
  const [avatarProposeMode, setAvatarProposeMode] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [deleteAvatarTarget, setDeleteAvatarTarget] = useState(null);
  const [proposeDeleteAvatar, setProposeDeleteAvatar] = useState(null);

  const load = useCallback(() => {
    if (!spaceId || !packageId) return;
    setLoading(true);
    Promise.all([
      getItems().catch(() => null),
      getItemTypes().catch(() => null),
      getTaxCategories().catch(() => null),
      getPackageById(packageId).catch(() => null),
      getPackageItems(packageId).catch(() => null),
    ]).then(([itemsData, itTypes, taxData, pkgData, pkgItemsData]) => {
      if (Array.isArray(itemsData)) setItems(itemsData);
      if (Array.isArray(itTypes)) setItemTypes(itTypes);
      if (Array.isArray(taxData)) setTaxCategories(taxData);
      setPkg(pkgData ?? null);
      if (Array.isArray(pkgItemsData)) setPackageItems(pkgItemsData);
    }).finally(() => setLoading(false));
  }, [spaceId, packageId]);

  useEffect(() => { load(); }, [load]);

  const packageByItemId = useMemo(() => {
    const map = new Map();
    for (const pi of packageItems) map.set(pi.itemId, pi);
    return map;
  }, [packageItems]);

  const avatarItems = useMemo(
    () => items.filter((item) => item.itemTypeId === AVATAR_ITEM_TYPE_ID),
    [items]
  );

  // Tab 1 picker: only approved/live avatars are selectable — same "live" rule
  // Items.jsx uses for its own Live tab, so "approved" means the same thing everywhere.
  const eligibleItems = useMemo(
    () => filterVersionedList(buildVersionedList(avatarItems, 'itemId'), 'live'),
    [avatarItems]
  );

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return eligibleItems;
    return eligibleItems.filter((item) => {
      const name = item.nameLocalizations?.values?.[0]?.value ?? item.name ?? '';
      return name.toLowerCase().includes(q) || (item.sku ?? '').toLowerCase().includes(q);
    });
  }, [eligibleItems, search]);

  // Tab 2 management list — same shape as Items.jsx: versioned list + per-tab
  // counts + status-filtered view, so Live/Pending/Draft/Rejected behave identically.
  const avatarVersioned = useMemo(() => buildVersionedList(avatarItems, 'itemId'), [avatarItems]);
  const avatarCounts    = useMemo(() => countVersionedTabs(avatarItems, 'itemId'), [avatarItems]);
  const avatarFilteredByStatus = useMemo(
    () => filterVersionedList(avatarVersioned, avatarStatusTab),
    [avatarVersioned, avatarStatusTab]
  );

  const handleAdd = useCallback(async (itemId) => {
    setBusyItemId(itemId);
    try {
      await addPackageItem(packageId, itemId);
      toast.success('Item added — submit it for approval from the Packages tab to go live.');
      const refreshed = await getPackageItems(packageId).catch(() => null);
      if (Array.isArray(refreshed)) setPackageItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add the item to the package.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast, packageId]);

  const handleRemove = useCallback(async (itemId) => {
    setBusyItemId(itemId);
    try {
      const result = await removePackageItem(packageId, itemId);
      toast.success(result?.message || 'Item removed from the package.');
      const refreshed = await getPackageItems(packageId).catch(() => null);
      if (Array.isArray(refreshed)) setPackageItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove the item from the package.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast, packageId]);

  // ── Avatar create/edit/approval handlers (Tab 2) — mirrors Items.jsx's item flow ──
  const handleAddTaxClass = async (newClass) => {
    const created = await createTaxClass(newClass);
    setTaxCategories(prev => [...prev, created]);
    return created;
  };

  const handleAvatarSave = async (formData) => {
    try {
      if (avatarProposeMode) {
        const itemId = formData.itemId ?? editingAvatar?.itemId;
        const itemName = formData.nameLocalizations?.values?.[0]?.value ?? formData.name ?? `Item ${itemId}`;
        await submitApproval({
          contentType: CONTENT_TYPES.ITEM,
          contentId: itemId,
          contentName: itemName,
          actionType: 'edit',
          proposedData: JSON.stringify(formData),
          proposedSnapshot: JSON.stringify(formData),
        });
        toast.success('Edit submitted for approval!');
        setAvatarProposeMode(false);
        setAvatarModalOpen(false);
        setEditingAvatar(null);
        const refreshed = await getItemById(itemId).catch(() => null);
        if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
        flashAvatarTab('pending');
      } else if (formData.itemId) {
        const { itemId, sku, ...updatePayload } = formData;
        const updated = await updateItem(itemId, updatePayload);
        setItems(prev => prev.map(it => it.itemId === itemId ? { ...it, ...updated } : it));
        toast.success('Avatar updated successfully.');
        setAvatarModalOpen(false);
        setEditingAvatar(null);
        flashAvatarTab('draft');
      } else {
        const { itemId: _id, ...createPayload } = formData;
        const created = await createItem(createPayload);
        setItems(prev => [...prev, created]);
        toast.success('Avatar created — submit it for approval to make it selectable in "Select Item for Package".');
        setAvatarModalOpen(false);
        setEditingAvatar(null);
        flashAvatarTab('draft');
      }
    } catch {
      // global interceptor already shows the error toast
    }
  };

  const handleAvatarEditRejected = (proposedSnap, liveItemId) => {
    const liveItem = items.find(it => it.itemId === liveItemId);
    setEditingAvatar({ ...(liveItem ?? {}), ...proposedSnap, itemId: liveItemId });
    setAvatarProposeMode(true);
    setAvatarModalReadOnly(false);
    setAvatarModalOpen(true);
  };

  const confirmAvatarProposeDelete = async () => {
    if (!proposeDeleteAvatar) return;
    try {
      const itemName = proposeDeleteAvatar.nameLocalizations?.values?.[0]?.value ?? proposeDeleteAvatar.name ?? `Item ${proposeDeleteAvatar.itemId}`;
      await submitApproval({
        contentType: CONTENT_TYPES.ITEM,
        contentId: proposeDeleteAvatar.itemId,
        contentName: itemName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteAvatar(null);
      const data = await getItems().catch(() => null);
      if (Array.isArray(data)) setItems(data);
      flashAvatarTab('pending');
    } catch {
      setProposeDeleteAvatar(null);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!deleteAvatarTarget) return;
    try {
      await deleteItem(deleteAvatarTarget.itemId);
      setItems(prev => prev.filter(it => it.itemId !== deleteAvatarTarget.itemId));
      toast.success('Avatar removed from catalog.');
    } catch {
      // global interceptor already shows the error toast
    } finally {
      setDeleteAvatarTarget(null);
    }
  };

  // No package draft to add items to — send the admin back to create one first.
  if (!loading && !pkg) {
    return (
      <ContentArea title="Add Package Items" icon={<Inventory2OutlinedIcon />}>
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
          <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155', mb: 1 }}>
            <LocalText>Package not found</LocalText>
          </Typography>
          <Typography fontSize={12} sx={{ color: '#94a3b8', mb: 2.5 }}>
            <LocalText>Create a package on the Packages tab before selecting items.</LocalText>
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/packages')}
            sx={{ background: '#0a326d', color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none', borderRadius: 2, px: 3, py: 1, '&:hover': { background: '#0d3d85' } }}
          >
            <LocalText>Back to Packages</LocalText>
          </Button>
        </Box>
      </ContentArea>
    );
  }

  const pkgName = pkg?.nameLocalizations?.values?.[0]?.value ?? 'Package';

  return (
    <ContentArea title={`Add Items — ${pkgName}`} icon={<Inventory2OutlinedIcon />}>
      {/* One toolbar bar — same look as Items.jsx's filter bar (white card, everything on one row) */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: '12px',
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          p: 1.5,
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
          mb: 2,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: '#2563eb' },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13, minHeight: 40, py: 0.5, px: 1.5, color: '#64748b' },
            '& .Mui-selected': { color: '#0a326d !important' },
          }}
        >
          <Tab value="select" label="Select Item for Package" />
          <Tab value="create" label="Create New Avatar" />
        </Tabs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          {tab === 'select' && (
            <TextField
              size="small"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ bgcolor: 'white', borderRadius: '10px', minWidth: 220, '& fieldset': { borderRadius: '10px' } }}
            />
          )}
          {tab === 'create' && canCreateAvatar && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => { setEditingAvatar(null); setAvatarModalReadOnly(false); setAvatarProposeMode(false); setAvatarModalOpen(true); }}
              sx={{
                background: '#2563eb', color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none',
                borderRadius: 2, px: 2.5, py: 1.125, boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                '&:hover': { background: '#1d4ed8', boxShadow: '0 4px 12px rgba(37,99,235,0.45)' },
              }}
            >
              <LocalText>Create New Avatar</LocalText>
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate('/packages')}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: 12 }}
          >
            <LocalText>Done — Back to Packages</LocalText>
          </Button>
        </Box>
      </Box>

      {tab === 'select' ? (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
              <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
                <LocalText>{eligibleItems.length === 0 ? 'No approved avatars yet — create one in the "Create New Avatar" tab first.' : 'No items match your search.'}</LocalText>
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
              {filteredItems.map((item) => (
                <PackageItemCard
                  key={item.itemId}
                  item={item}
                  itemTypes={itemTypes}
                  packageEntry={packageByItemId.get(item.itemId)}
                  onAdd={handleAdd}
                  onRemove={handleRemove}
                  canEdit={canEdit}
                  busy={busyItemId === item.itemId}
                />
              ))}
            </Box>
          )}
        </>
      ) : (
        <>
          <StatusTabs
            items={avatarItems}
            counts={avatarCounts}
            value={avatarStatusTab}
            onChange={setAvatarStatusTab}
            sx={{ mb: 2, mt: 0 }}
            highlightTab={avatarHighlight.tab}
            highlightToken={avatarHighlight.token}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : avatarFilteredByStatus.length === 0 ? (
            <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
              <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
                <LocalText>{avatarVersioned.length === 0 ? 'No avatars created yet.' : 'No avatars in this status.'}</LocalText>
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2.5,
              }}
            >
              {avatarFilteredByStatus.map((item) => (
                <ItemCard
                  key={`${item.itemId}-${item._viewType}`}
                  item={item}
                  itemTypes={itemTypes}
                  onEdit={(it, proposeMode) => { setEditingAvatar(it); setAvatarModalReadOnly(false); setAvatarProposeMode(!!proposeMode); setAvatarModalOpen(true); }}
                  onView={(it) => { setEditingAvatar(it); setAvatarModalReadOnly(true); setAvatarProposeMode(false); setAvatarModalOpen(true); }}
                  onDelete={(it, proposeMode) => proposeMode ? setProposeDeleteAvatar(it) : setDeleteAvatarTarget(it)}
                  onEditRejected={canCreateAvatar ? handleAvatarEditRejected : undefined}
                  onSuccess={async (targetTab) => {
                    const refreshed = await getItemById(item.itemId ?? item._liveId).catch(() => null);
                    if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
                    if (targetTab) flashAvatarTab(targetTab);
                  }}
                  showActions={canCreateAvatar}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <ItemModal
        open={avatarModalOpen}
        onClose={() => { setAvatarModalOpen(false); setEditingAvatar(null); setAvatarModalReadOnly(false); setAvatarProposeMode(false); }}
        onSave={handleAvatarSave}
        initialData={editingAvatar}
        lockTypeId={AVATAR_ITEM_TYPE_ID}
        itemTypes={itemTypes}
        taxCategories={taxCategories}
        onAddTaxClass={handleAddTaxClass}
        readOnly={avatarModalReadOnly}
      />

      <ConfirmModal
        open={Boolean(deleteAvatarTarget)}
        onCancel={() => setDeleteAvatarTarget(null)}
        onConfirm={handleDeleteAvatar}
        severity="danger"
        title="Delete Avatar"
        message={`Remove "${deleteAvatarTarget?.nameLocalizations?.values?.[0]?.value ?? deleteAvatarTarget?.name ?? 'this avatar'}" from the catalog?`}
        subtitle="This action cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmModal
        open={Boolean(proposeDeleteAvatar)}
        onCancel={() => setProposeDeleteAvatar(null)}
        onConfirm={confirmAvatarProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${proposeDeleteAvatar?.nameLocalizations?.values?.[0]?.value ?? proposeDeleteAvatar?.name ?? 'this avatar'}"? The item remains available until an approver approves.`}
        confirmLabel="Submit for Approval"
      />
    </ContentArea>
  );
}
